import React, { useState } from 'react';
import { removeTeamMember } from '@api/team-member';
import SuccessNotificationBanner from '../SuccessNotificationBanner';
import './styles.scss';

interface RemoveTeamMemberProps {
  selectedTeamMemberId: string;
  partnerCarehomeId: string;
  validateTeamMemberSelection: () => boolean;
  setSelectedTeamMemberId: (id: string) => void;
  onFormSubmit: () => void;
}

const RemoveTeamMember: React.FC<RemoveTeamMemberProps> = ({
  selectedTeamMemberId,
  partnerCarehomeId,
  validateTeamMemberSelection,
  onFormSubmit,
  setSelectedTeamMemberId,
}) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [removeSuccess, setRemoveSuccess] = useState(false);

  const showNotificationBanner = () => {
    const isValid = validateTeamMemberSelection();
    if (!isValid) {
      return;
    }
    setVisible(true);
  };

  const handleOk = async () => {
    setLoading(true);
    try {
      const response = await removeTeamMember(
        selectedTeamMemberId,
        partnerCarehomeId
      );
      if (response && response.status === 'success') {
        onFormSubmit();
        setSelectedTeamMemberId('');

        setRemoveSuccess(true);
      } else {
        throw new Error(
          response.user_message || 'Failed to remove team member'
        );
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
      setVisible(false);
    }
  };

  const handleCancel = () => {
    setVisible(false);
  };

  return (
    <div className="remove-team-member">
      {removeSuccess && (
        <SuccessNotificationBanner
          content="Removed team member."
          title=""
          duration={3000}
        />
      )}
      <button
        type="submit"
        className="govuk-button govuk-button--warning"
        data-module="govuk-button"
        onClick={showNotificationBanner}
      >
        Remove Team Member
      </button>
      {visible && (
        <div
          className="govuk-notification-banner remove-team-member__banner"
          role="region"
          aria-labelledby="govuk-notification-banner-title"
          data-module="govuk-notification-banner"
        >
          <div className="govuk-notification-banner__header">
            <h2
              className="govuk-notification-banner__title"
              id="govuk-notification-banner-title"
            >
              Important
            </h2>
          </div>
          <div className="govuk-notification-banner__content">
            <p className="govuk-notification-banner__heading">
              Are you sure that you would like to remove this person from your
              place of work?
            </p>
            <p className="govuk-body">
              Once you confirm that this person should no longer have access
              they will no longer be able to view or edit any of the records.
              This action cannot be undone.
            </p>
            <p className="govuk-body">
              Notes and any information previously created by them will however
              remain in the system and be viewable and editable.
            </p>
            <div className="govuk-button-group">
              <button
                type="submit"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                onClick={handleCancel}
              >
                No, I do not want to remove this person
              </button>
              <button
                type="submit"
                className="govuk-button govuk-button--warning"
                data-module="govuk-button"
                onClick={handleOk}
                disabled={loading}
                aria-disabled={loading}
              >
                Yes I am sure
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemoveTeamMember;
